import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import parseHttpError from "@/utils/parseHttpError";
import { useAuth } from "@/contexts/AuthContext";
import { KYCService, PropertyService, UserService } from "@/services";
import { BusinessDocument, BusinessDocumentType } from "@/types/business";
import {
  ACCEPTED_FILE_FORMATS,
  MAX_FILE_SIZE_MB,
  getDocumentNumberRule,
  normalizeKycStatus,
  sanitizeDocumentNumber,
  StagedDocument,
} from "./constants";

export const useKYCDocuments = ({
  onAllDocumentsUploaded,
}: {
  onAllDocumentsUploaded?: () => void;
} = {}) => {
  const {
    user,
    businessDetails,
    isBusinessLoading,
    refreshUser,
    refreshBusinessDetails,
    isDeveloper,
  } = useAuth();

  // Documents now come from the auth context business details payload, which
  // the backend enriched with the submitted documents. No separate
  // /business/documents fetch is needed anymore.
  const documents = useMemo<BusinessDocument[]>(
    () => businessDetails?.documents ?? [],
    [businessDetails],
  );

  // Locally staged documents awaiting the unified "Submit KYC documents" click.
  const [stagedDocuments, setStagedDocuments] = useState<StagedDocument[]>([]);

  // Add-new form visibility
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);

  // Add-new form state
  const [documentType, setDocumentType] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");

  // Legal name state (must match govt issued ID for verification)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Edit form state
  const [editingDocument, setEditingDocument] =
    useState<BusinessDocument | null>(null);
  const [editNumber, setEditNumber] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");

  // Delete confirmation state
  const [documentToDelete, setDocumentToDelete] =
    useState<BusinessDocument | null>(null);

  // File upload state
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [editFile, setEditFile] = useState<File | null>(null);
  const [isEditUploading, setIsEditUploading] = useState(false);
  const [editUploadedFileUrl, setEditUploadedFileUrl] = useState<string | null>(
    null,
  );

  const isVerified = normalizeKycStatus(user?.kyc_status) === "verified";

  // Guard against the modal opening before the auth context finished loading
  // business details (e.g. on a hard reload straight onto the profile page).
  useEffect(() => {
    if (!isBusinessLoading && !businessDetails && user) {
      refreshBusinessDetails().catch(() => undefined);
    }
  }, [businessDetails, isBusinessLoading, user, refreshBusinessDetails]);

  // Sync legal name fields from the authenticated user's full name
  useEffect(() => {
    const nameParts = (user?.full_name ?? "").split(" ").filter(Boolean);
    setFirstName(nameParts[0] ?? "");
    setLastName(nameParts.slice(1).join(" "));
    setEditFirstName(nameParts[0] ?? "");
    setEditLastName(nameParts.slice(1).join(" "));
  }, [user?.full_name]);

  const buildUserProfilePayload = (first: string, last: string) => ({
    first_name: first.trim(),
    last_name: last.trim(),
    phone_number: user?.phone_number ?? "",
    city: user?.city ?? "",
    state: user?.state ?? "",
  });

  const serverByType = useMemo(() => {
    const map: Record<string, BusinessDocument> = {};
    documents.forEach((doc) => {
      if (doc.type) map[String(doc.type).toLowerCase()] = doc;
    });
    return map;
  }, [documents]);

  const stagedByType = useMemo(() => {
    const map: Record<string, StagedDocument> = {};
    stagedDocuments.forEach((doc) => {
      map[String(doc.type).toLowerCase()] = doc;
    });
    return map;
  }, [stagedDocuments]);

  const generateFileKey = (file: File) => {
    const timestamp = new Date().getTime();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = file.name.split(".").pop();
    return `properties/${timestamp}-${randomString}.${extension}`;
  };

  const uploadFile = async (file: File): Promise<string | null> => {
    try {
      const key = generateFileKey(file);
      const { data } = await PropertyService.GetUploadUrl(key);
      const { upload_url, metadata } = data?.data;
      await fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      return metadata?.url ?? null;
    } catch (error) {
      console.error("Error uploading file:", error);
      return null;
    }
  };

  const handleFile = async (newFile: File | null, isEdit = false) => {
    if (!newFile) return;
    if (!ACCEPTED_FILE_FORMATS.includes(newFile.type)) {
      toast.error(`File type ${newFile.type} is not supported`);
      return;
    }
    if (newFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File is larger than ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    if (isEdit) {
      setIsEditUploading(true);
      setEditFile(newFile);
      const url = await uploadFile(newFile);
      setEditUploadedFileUrl(url);
      setIsEditUploading(false);
    } else {
      setIsUploading(true);
      setFile(newFile);
      const url = await uploadFile(newFile);
      setUploadedFileUrl(url);
      setIsUploading(false);
    }
  };

  const removeFile = () => {
    setFile(null);
    setUploadedFileUrl(null);
  };

  const removeEditFile = () => {
    setEditFile(null);
    setEditUploadedFileUrl(null);
  };

  const resetAddForm = () => {
    setDocumentType("");
    setDocumentNumber("");
    setFile(null);
    setUploadedFileUrl(null);
  };

  const handleDocumentNumberChange = (value: string) => {
    setDocumentNumber(sanitizeDocumentNumber(value, documentType));
  };

  const handleEditNumberChange = (value: string) => {
    setEditNumber(sanitizeDocumentNumber(value, editingDocument?.type));
  };

  const startAddDocument = (type: string) => {
    setDocumentType(type);
    setDocumentNumber("");
    setFile(null);
    setUploadedFileUrl(null);
    setIsAddFormOpen(true);
  };

  const cancelAddDocument = () => {
    resetAddForm();
    setIsAddFormOpen(false);
  };

  const stageDocument = () => {
    const type = documentType.toLowerCase() as BusinessDocumentType;
    const rule = getDocumentNumberRule(documentType);
    if (rule && !rule.pattern.test(documentNumber.trim())) {
      toast.error(rule.error);
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Please provide your first and last name");
      return;
    }
    if (!uploadedFileUrl) {
      toast.error("Please upload the document file");
      return;
    }

    setStagedDocuments((prev) => {
      const next = prev.filter((doc) => String(doc.type) !== String(type));
      return [
        ...next,
        { type, number: documentNumber.trim(), upload_url: uploadedFileUrl },
      ];
    });
    toast.success(
      `${documentType.toUpperCase()} document added. Submit your documents when ready.`,
    );
    resetAddForm();
    setIsAddFormOpen(false);
  };

  const removeStagedDocument = (type: BusinessDocumentType) => {
    setStagedDocuments((prev) =>
      prev.filter((doc) => String(doc.type) !== String(type)),
    );
    toast.success(`${String(type).toUpperCase()} document removed`);
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!firstName.trim() || !lastName.trim()) {
        throw new Error("Please provide your first and last name");
      }
      if (stagedDocuments.length === 0) {
        throw new Error("No documents to submit");
      }
      await UserService.updateUserProfile(
        buildUserProfilePayload(firstName, lastName),
      );
      for (const doc of stagedDocuments) {
        await KYCService.addBusinessDocument({
          type: doc.type,
          number: doc.number,
          upload_url: doc.upload_url,
        });
      }
    },
    onSuccess: async () => {
      toast.success("KYC documents submitted for verification");
      setStagedDocuments([]);
      refreshUser().catch(() => undefined);
      await refreshBusinessDetails().catch(() => undefined);
      onAllDocumentsUploaded?.();
    },
    onError: (error: any) => {
      toast.error(error?.message || "Request failed");
    },
  });

  const startEdit = (doc: BusinessDocument) => {
    setEditingDocument(doc);
    setEditNumber(
      sanitizeDocumentNumber(doc.identification_number ?? "", doc.type),
    );
    setEditFile(null);
    setEditUploadedFileUrl(null);
    const nameParts = (user?.full_name ?? "").split(" ").filter(Boolean);
    setEditFirstName(nameParts[0] ?? "");
    setEditLastName(nameParts.slice(1).join(" "));
  };

  const cancelEdit = () => {
    setEditingDocument(null);
    setEditNumber("");
    setEditFile(null);
    setEditUploadedFileUrl(null);
    setEditFirstName("");
    setEditLastName("");
  };

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingDocument) return;
      if (!editFirstName.trim() || !editLastName.trim()) {
        throw new Error("Please provide your first and last name");
      }
      const rule = getDocumentNumberRule(editingDocument.type);
      if (rule && !rule.pattern.test(editNumber.trim())) {
        throw new Error(rule.error);
      }
      await UserService.updateUserProfile(
        buildUserProfilePayload(editFirstName, editLastName),
      );
      const response = await KYCService.editBusinessDocument(
        editingDocument.id,
        {
          number: editNumber,
          upload_url: editUploadedFileUrl ?? undefined,
        },
      );
      return response?.data;
    },
    onSuccess: async () => {
      toast.success("Document updated successfully");
      refreshUser().catch(() => undefined);
      cancelEdit();
      await refreshBusinessDetails().catch(() => undefined);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Request failed");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      await KYCService.deleteBusinessDocument(documentId);
    },
    onSuccess: async () => {
      toast.success("Document deleted successfully");
      setDocumentToDelete(null);
      if (editingDocument) cancelEdit();
      await refreshBusinessDetails().catch(() => undefined);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to delete document");
    },
  });

  return {
    documents,
    isLoading: isBusinessLoading,
    isVerified,
    isDeveloper,
    isAddFormOpen,
    setIsAddFormOpen,
    serverByType,
    stagedByType,
    stagedDocuments,
    // Add/stage form
    documentType,
    setDocumentType,
    documentNumber,
    setDocumentNumber,
    handleDocumentNumberChange,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    file,
    isUploading,
    uploadedFileUrl,
    handleFile,
    removeFile,
    startAddDocument,
    cancelAddDocument,
    stageDocument,
    removeStagedDocument,
    submitMutation,
    // Edit form
    editingDocument,
    editNumber,
    setEditNumber,
    handleEditNumberChange,
    editFirstName,
    setEditFirstName,
    editLastName,
    setEditLastName,
    editFile,
    isEditUploading,
    removeEditFile,
    startEdit,
    cancelEdit,
    // Delete
    documentToDelete,
    setDocumentToDelete,
    // Mutations
    editMutation,
    deleteMutation,
  };
};
